/**
 * Split year-over-year chart (WS3): confirmed refinances vs returning clients.
 * Confirmed refinances are the refinance-rate numerator; returning clients are
 * charted alongside but never folded into the rate. Mounted in BOTH
 * RefinanceIntelligence page variants.
 */
import React from 'react';
import { BarChart3 } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { YearlyRefinanceData } from '../../hooks/useRefinanceData';

interface RefinanceReturningChartProps {
  yearlyBreakdown: YearlyRefinanceData[] | undefined;
}

const RefinanceReturningChart: React.FC<RefinanceReturningChartProps> = ({ yearlyBreakdown }) => {
  if (!yearlyBreakdown || yearlyBreakdown.length === 0) return null;

  const data = [...yearlyBreakdown]
    .sort((a, b) => a.year - b.year)
    .map((y) => ({
      year: y.year,
      confirmedRefinances: y.confirmedRefinances ?? 0,
      returningClients: y.returningClients ?? 0,
    }));

  return (
    <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 className="w-5 h-5 text-purple-400" />
        <h3 className="text-lg font-semibold text-white">Refinances vs Returning Clients by Year</h3>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Confirmed refinances drive the refinance rate; returning clients are tracked separately.
      </p>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="year" stroke="#9CA3AF" />
            <YAxis stroke="#9CA3AF" allowDecimals={false} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
              labelStyle={{ color: '#F9FAFB' }}
              cursor={{ fill: 'rgba(55, 65, 81, 0.3)' }}
              formatter={(value: number, name: string) => {
                if (name === 'confirmedRefinances') return [value, 'Confirmed Refinances'];
                if (name === 'returningClients') return [value, 'Returning Clients'];
                return [value, name];
              }}
            />
            <Legend
              wrapperStyle={{ color: '#9CA3AF' }}
              formatter={(value) => {
                if (value === 'confirmedRefinances') return 'Confirmed Refinances';
                if (value === 'returningClients') return 'Returning Clients';
                return value;
              }}
            />
            <Bar dataKey="confirmedRefinances" fill="#22D3EE" name="confirmedRefinances" />
            <Bar dataKey="returningClients" fill="#A78BFA" name="returningClients" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RefinanceReturningChart;
